import { Button, Panel, Tooltip } from "@mixitone/components";
import TrophyIcon from "@mixitone/icons/trophy.svg";
import CalendarIcon from "@mixitone/icons/calendar.svg";
import UserIcon from "@mixitone/icons/user.svg";
import { Database } from "@mixitone/models/database.types";
import { ApplicationView } from "@mixitone/mvc";
import { dateAndTime, toOrdinal } from "@mixitone/util";
import React from "react";
import { DashLayout } from "../../Components/DashLayout";
import { MessageBanner } from "../../Components/MessageBanner";
import { injectParams } from "../../lib/injectParams";
import { CompetitionDetailPageController } from "./controllers/CompetitionDetailPageController";
import { CompetitionResultsPanel } from "./components/CompetitionResultsPanel";
import { PartnerInvitePanel } from "./components/PartnerInvitePanel";
import { Link } from "react-router-dom";

interface InjectedProps {
  params: { portalSlug: string; competitionId: string };
}

const ControlledCompetitionDetailPage: React.FC<InjectedProps> = () => {
  const controller = CompetitionDetailPageController.use();
  const {
    competition,
    session,
    registration,
    player,
    match,
    matchTimer,
    historicMatches,
    loadingHistoricMatches,
    loading,
    error,
    placement,
    winners,
    partnerLink,
    partnerEmail,
    partnerInviteSent,
    sendingPartnerInvite,
  } = controller.state;

  if (loading) {
    return (
      <DashLayout title="Competition">
        <div className="flex flex-col gap-6 pb-6">
          <div className="animate-pulse">
            <div className="h-10 mb-4 rounded bg-slate-200"></div>
            <div className="h-20 mb-2 rounded bg-slate-200"></div>
          </div>
        </div>
      </DashLayout>
    );
  }

  if (!competition || !session || !registration || !player) {
    return (
      <DashLayout title="Competition">
        <div className="flex flex-col gap-6 pb-6">
          <Panel title="Not found" titleClassName="font-semibold">
            <p className="text-slate-600">Competition not found.</p>
          </Panel>
        </div>
      </DashLayout>
    );
  }

  const tournamentStatus = session.status as Database["public"]["Enums"]["tournament_session_status"];

  const displayDate = session.starts_at || session.ends_at;
  const [date, time] = displayDate ? dateAndTime(displayDate) : ["", ""];

  // Status display logic (same as CompetitionItem)
  type RegistrationStatus =
    | "completed"
    | "withdrawn"
    | "payment_required"
    | "waiting_partner"
    | "waiting_confirmation"
    | "confirmed";

  const statusColor: Record<RegistrationStatus, string> = {
    completed: "bg-green-50 text-green-700 ring-green-700/10",
    withdrawn: "bg-red-50 text-red-700 ring-red-700/10",
    payment_required: "bg-yellow-50 text-yellow-700 ring-yellow-700/10 hover:bg-yellow-100",
    waiting_partner: "bg-orange-50 text-orange-700 ring-orange-700/10",
    waiting_confirmation: "bg-blue-50 text-blue-700 ring-blue-700/10",
    confirmed: "bg-green-50 text-green-700 ring-green-700/10",
  };

  const statusText: Record<RegistrationStatus, string> = {
    completed: "Completed",
    withdrawn: "Withdrawn",
    payment_required: "Payment required",
    waiting_partner: "Waiting for partner",
    waiting_confirmation: "Waiting confirmation",
    confirmed: "Confirmed",
  };

  const statusTooltip: Record<RegistrationStatus, string> = {
    completed: "This competition is completed",
    withdrawn: "You have withdrawn from this competition",
    payment_required: "Payment is required to confirm your entry",
    waiting_partner: "Waiting for your partner to join. Share the invite link below.",
    waiting_confirmation: "Waiting for your entry to be confirmed",
    confirmed: "Your entry is confirmed",
  };

  // Get display status - show "In progress" instead of "Confirmed" when tournament session is in registration or active
  const displayStatus = registration.status as RegistrationStatus;
  const isTournamentActive = tournamentStatus === "registration" || tournamentStatus === "active";
  const displayStatusText =
    registration.status === "confirmed" && isTournamentActive
      ? "In progress"
      : statusText[registration.status as RegistrationStatus];
  const displayStatusTooltip =
    registration.status === "confirmed" && isTournamentActive
      ? "The tournament is in progress"
      : statusTooltip[registration.status as RegistrationStatus];
  const isCompleted = competition.status === "completed" || session.status === "completed";

  return (
    <DashLayout title={competition.name || "Competition"}>
      <div className="flex flex-col gap-6 pb-6">
        {error && (
          <MessageBanner message={error} type="error" onClose={() => controller.actionClearError()} />
        )}

        {/* Header: Competition name, date, status, partner names */}
        <Panel title={competition.name || "Competition"} titleClassName="font-semibold">
          <div className="flex flex-col gap-4">
            <div className="flex items-start justify-between gap-4">
              {displayDate && (
                <div className="flex items-center gap-2.5 text-sm text-slate-600">
                  <CalendarIcon className="flex-shrink-0 w-4 h-4 text-slate-400" />
                  <span>
                    <span className="font-medium">{date}</span>
                    {time && <span className="text-slate-500"> @ {time}</span>}
                  </span>
                </div>
              )}
              <div className="flex items-center flex-shrink-0 gap-2">
                <span
                  className={`${statusColor[displayStatus]} inline-flex items-center whitespace-nowrap rounded-full px-3 py-1.5 text-xs font-medium ring-1 ring-inset`}
                >
                  {displayStatusText}
                </span>
                <Tooltip icon>{displayStatusTooltip}</Tooltip>
              </div>
            </div>
            {registration.partner_names && registration.partner_names.length > 0 && (
              <div className="flex items-center gap-2.5 text-sm text-slate-600">
                <UserIcon className="flex-shrink-0 w-4 h-4 text-slate-400" />
                <span>
                  <span className="text-slate-500">
                    Partner{registration.partner_names.length > 1 ? "s" : ""}:{" "}
                  </span>
                  <span className="font-medium">{registration.partner_names.join(", ")}</span>
                </span>
              </div>
            )}
          </div>
          {isCompleted && session?.portal_slug && session.is_public && (
            <div className="flex justify-end pt-3 border-t border-slate-100">
              <Link
                to={`/tournament/${session.portal_slug}`}
                className="text-sm font-medium text-blue-600 transition-colors hover:text-blue-700"
                onClick={(e) => e.stopPropagation()}
              >
                View Summary →
              </Link>
            </div>
          )}
        </Panel>

        {registration.status === "waiting_partner" && partnerLink && (
          <Panel title="Waiting for partner" titleClassName="font-semibold">
            <PartnerInvitePanel
              partnerLink={partnerLink}
              partnerEmail={partnerEmail}
              partnerInviteSent={partnerInviteSent}
              sendingPartnerInvite={sendingPartnerInvite}
              onPartnerEmailChange={(email) => {
                controller.state.partnerEmail = email;
              }}
              onSendInvite={() => controller.actionSendPartnerInvite()}
            />
          </Panel>
        )}

        {placement && placement.place !== null && (
          <Panel title="Congratulations!" titleClassName="font-semibold">
            <div className="flex items-center gap-2.5 text-sm text-slate-600">
              <TrophyIcon className="flex-shrink-0 w-6 h-6 fill-yellow-500" />
              <span>
                <span className="font-medium">You won </span>
                <span className="font-bold">{toOrdinal(placement.place)} </span>
                <span className="font-medium">place{placement.division ? "" : "!"}</span>
                {placement.division ? (
                  <>
                    <span className="font-medium"> in </span>
                    <span className="font-medium text-blue-500"> Division {placement.division}</span>
                  </>
                ) : null}
              </span>
            </div>
          </Panel>
        )}

        {isCompleted && winners.length > 0 && (
          <CompetitionResultsPanel
            competitionName={competition.name || "This competition"}
            winners={winners}
          />
        )}

        {/* Content based on tournament status */}
        {["setup", "entry_open"].includes(tournamentStatus) && (
          <Panel titleClassName="font-semibold">
            <div className="flex flex-col items-center gap-4 py-8">
              <CalendarIcon className="w-16 h-16 text-slate-400" />
              {displayDate && (
                <div className="text-center">
                  <p className="text-lg font-medium text-slate-900">{date}</p>
                  {time && <p className="text-sm text-slate-600">{time}</p>}
                </div>
              )}
              <p className="text-sm text-center text-slate-600">
                The tournament has not yet started. Check back later for more information.
              </p>
              {/* TODO: Add withdraw button as separate subtask */}
            </div>
          </Panel>
        )}

        {tournamentStatus === "registration" && (
          <Panel title="Check in" titleClassName="font-semibold">
            {player.checked_in_at ? (
              <div className="py-4">
                <p className="text-sm text-slate-600">
                  Thank you for checking in! We'll let you know when your match is ready.
                </p>
              </div>
            ) : (
              <div className="flex flex-col gap-4 py-4">
                <p className="text-sm text-slate-600">Please check in when you arrive at the tournament.</p>
                <Button onClick={() => controller.actionCheckIn()}>I've arrived</Button>
              </div>
            )}
          </Panel>
        )}

        {competition.status === "active" && !match && (
          <Panel title="Waiting for match" titleClassName="font-semibold">
            <div className="py-4">
              <p className="text-sm text-slate-600">
                Wait for your assigned court. We'll notify you when your match is ready.
              </p>
            </div>
          </Panel>
        )}

        {match && match.status === "ready" && match.court_id && (
          <Panel title="Your match" titleClassName="font-semibold">
            <div className="flex flex-col gap-4 py-4">
              <div>
                <p className="text-sm font-medium text-slate-900">Court {match.court_id}</p>
                {match.opposing_entry && (
                  <p className="mt-2 text-sm text-slate-600">
                    <span className="text-slate-500">Opponents: </span>
                    <span className="font-medium">{match.opposing_entry.player_names.join(" & ")}</span>
                  </p>
                )}
              </div>
              <Button onClick={() => controller.actionStartMatch()} kind="accent">
                We're starting
              </Button>
            </div>
          </Panel>
        )}

        {match && match.status === "in_progress" && (
          <Panel title="Match in progress" titleClassName="font-semibold">
            <div className="flex flex-col gap-4 py-4">
              {match.court_id && (
                <div>
                  <p className="text-sm font-medium text-slate-900">Court {match.court_id}</p>
                  {match.opposing_entry && (
                    <p className="mt-2 text-sm text-slate-600">
                      <span className="text-slate-500">Opponents: </span>
                      <span className="font-medium">{match.opposing_entry.player_names.join(" & ")}</span>
                    </p>
                  )}
                </div>
              )}
              {matchTimer !== null && (
                <div className="p-4 rounded-md bg-slate-50">
                  <p className="text-xs text-slate-500">Match Duration</p>
                  <p className="font-mono text-2xl font-semibold text-slate-900">
                    {controller.formatTimer(matchTimer)}
                  </p>
                </div>
              )}
              <Button onClick={() => controller.actionFinishMatch()} kind="danger">
                We've finished
              </Button>
            </div>
          </Panel>
        )}

        {/* Historic Matches Panel */}
        {historicMatches.length > 0 && (
          <Panel title="Match history" titleClassName="font-semibold">
            <div className="flex flex-col gap-4 py-4">
              {loadingHistoricMatches ? (
                <div className="animate-pulse">
                  <div className="h-20 rounded bg-slate-200"></div>
                </div>
              ) : (
                historicMatches.map((historicMatch) => {
                  const matchDate = historicMatch.completed_at
                    ? dateAndTime(historicMatch.completed_at)[0]
                    : historicMatch.started_at
                      ? dateAndTime(historicMatch.started_at)[0]
                      : null;

                  return (
                    <div
                      key={historicMatch.id}
                      className="flex flex-col gap-2 p-4 border rounded-md border-slate-200 bg-slate-50"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex flex-col gap-1">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium text-slate-900">
                              Round {(historicMatch.round ?? 0) + 1}
                            </span>
                            {historicMatch.group && (
                              <span className="text-xs text-slate-500">{historicMatch.group}</span>
                            )}
                            {historicMatch.division && (
                              <span className="text-xs text-blue-500">Division {historicMatch.division}</span>
                            )}
                          </div>
                          {historicMatch.opposing_entry && (
                            <p className="text-sm text-slate-600">
                              <span className="text-slate-500">vs. </span>
                              <span className="font-medium">
                                {historicMatch.opposing_entry.player_names.join(" & ")}
                              </span>
                            </p>
                          )}
                        </div>
                        {historicMatch.result && (
                          <span
                            className={`px-2 py-1 text-xs font-medium rounded ${
                              historicMatch.result === "win"
                                ? "bg-green-100 text-green-800"
                                : "bg-red-100 text-red-800"
                            }`}
                          >
                            {historicMatch.result === "win" ? "Win" : "Loss"}
                          </span>
                        )}
                      </div>
                      {matchDate && (
                        <div className="flex items-center gap-2 text-xs text-slate-500">
                          <CalendarIcon className="w-3 h-3" />
                          {matchDate && <span>{matchDate}</span>}
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          </Panel>
        )}
      </div>
    </DashLayout>
  );
};

const CompetitionDetailPage = injectParams(
  CompetitionDetailPageController.scope(ApplicationView(ControlledCompetitionDetailPage)),
);

export { CompetitionDetailPage };
